<template>
  <div>
    <el-upload ref="nativeUpload" action="" accept=".jpg,.jpeg,.png,.gif,.webp,.bmp,.mp4" :show-file-list="false" :disabled="disabled || items.length >= 9"
               :before-upload="beforeUpload" :http-request="httpRequest" :multiple="true">
      <el-button size="small" :disabled="disabled || items.length >= 9"><i class="el-icon-plus"></i> 上传图片 / 视频</el-button>
    </el-upload>
    <p class="hint">合计最多 9 个；图片不超过 10 MB，MP4 视频不超过 30 MB。</p>
    <div v-for="item in items" :key="item.uid" class="attachment">
      <img v-if="item.type === 'image'" :src="item.preview" :alt="item.name" />
      <video v-else :src="item.preview" controls preload="metadata" />
      <div class="attachment-info"><span>{{ item.name }}</span>
        <el-progress v-if="item.status === 'uploading'" :percentage="item.progress" :stroke-width="4" />
        <small v-else-if="item.status === 'ready'">上传完成</small><small v-else class="error" role="alert">{{ item.error }}</small>
        <el-button v-if="item.status === 'failed'" type="text" size="mini" :disabled="disabled" @click="retry(item)">重试上传</el-button>
        <el-button type="text" size="mini" :disabled="disabled" @click="remove(item)">移除</el-button>
      </div>
    </div>
  </div>
</template>

<script>
import { createComplaintUploadSession, uploadComplaintAttachment } from '@/api/complaintSidebar';

export default {
  name: 'ComplaintAttachments',
  props: { token: String, account: String, roleKey: String, disabled: Boolean },
  data: () => ({ items: [], sessionPromise: null, generation: 0 }),
  beforeDestroy() { this.clear(); },
  methods: {
    publish() { this.$emit('input', this.items.slice()); },
    httpRequest(options) {
      this.uploadFile(options);
      // Element 的请求对象约定；移除后由 active() 丢弃迟到结果。
      return { abort() {} };
    },
    beforeUpload(file) {
      const image = /\.(jpe?g|png|gif|webp|bmp)$/i.test(file.name) && /^image\//.test(file.type);
      const video = /\.mp4$/i.test(file.name) && file.type === 'video/mp4';
      const error = this.items.length >= 9 ? '图片和视频合计最多 9 个' : (!image && !video ? '请选择支持的图片或 MP4 视频'
        : file.size <= 0 ? '不能上传空文件' : file.size > (image ? 10 : 30) * 1024 * 1024 ? (image ? '图片不能超过 10 MB' : '视频不能超过 30 MB') : '');
      if (error) { this.$message.error(error); return false; }
      // 在 beforeUpload 同步占位，避免同时选择多文件突破数量限制。
      this.items.push({ uid: file.uid, name: file.name, type: image ? 'image' : 'video', raw: file,
        preview: URL.createObjectURL(file), status: 'uploading', progress: 0, id: '', error: '' });
      this.publish(); return true;
    },
    session() {
      if (!this.sessionPromise) this.sessionPromise = createComplaintUploadSession(this.token, this.account, this.roleKey);
      return this.sessionPromise;
    },
    async uploadFile(options) {
      const item = this.items.find(row => row.uid === options.file.uid);
      if (!item) return;
      const generation = this.generation;
      const active = () => generation === this.generation && this.items.includes(item);
      item.status = 'uploading'; item.error = ''; item.progress = 0; this.publish();
      try {
        const session = await this.session();
        if (!active()) return;
        const cover = item.type === 'video' ? await this.videoCover(item.raw) : null;
        if (!active()) return;
        const result = await uploadComplaintAttachment(session.upload_session, item.raw, event => {
          if (active() && event.total) item.progress = Math.min(99, Math.round(event.loaded * 100 / event.total));
        }, cover);
        if (!active()) return;
        item.id = result.id; item.status = 'ready'; item.progress = 100;
        if (options.onSuccess) options.onSuccess(result);
      } catch (error) {
        if (!active()) return;
        this.sessionPromise = null;
        item.status = 'failed'; item.error = (error.response && error.response.data && (error.response.data.message || error.response.data.msg)) || error.message || '上传失败，请重试';
        if (options.onError) options.onError(error);
      } finally { if (active()) this.publish(); }
    },
    retry(item) { if (!this.disabled && item.status === 'failed') this.uploadFile({ file: item.raw }); },
    videoCover(file) {
      return new Promise(resolve => {
        const video = document.createElement('video'); const url = URL.createObjectURL(file);
        let finished = false;
        const finish = blob => { if (finished) return; finished = true; window.clearTimeout(timer); video.onloadeddata = null; video.onerror = null; video.removeAttribute('src'); video.load(); URL.revokeObjectURL(url); resolve(blob); };
        const timer = window.setTimeout(() => finish(null), 5000);
        video.muted = true; video.playsInline = true; video.preload = 'auto';
        video.onerror = () => finish(null);
        video.onloadeddata = () => {
          try {
            if (!video.videoWidth || !video.videoHeight) return finish(null);
            const canvas = document.createElement('canvas'); const scale = Math.min(1, 960 / Math.max(video.videoWidth, video.videoHeight));
            canvas.width = Math.max(1, Math.round(video.videoWidth * scale)); canvas.height = Math.max(1, Math.round(video.videoHeight * scale));
            canvas.getContext('2d').drawImage(video, 0, 0, canvas.width, canvas.height);
            canvas.toBlob(finish, 'image/jpeg', 0.85);
          } catch (error) { finish(null); }
        };
        video.src = url;
      });
    },
    remove(item) { if (!this.disabled) { URL.revokeObjectURL(item.preview); this.items = this.items.filter(row => row !== item); if (this.$refs.nativeUpload) this.$refs.nativeUpload.handleRemove(item.raw); this.publish(); } },
    clear() { this.generation++; this.items.forEach(item => URL.revokeObjectURL(item.preview)); this.items = []; this.sessionPromise = null; if (this.$refs.nativeUpload) this.$refs.nativeUpload.clearFiles(); this.publish(); },
  },
};
</script>

<style scoped>
.hint { color: #8791a3; font-size: 11px; line-height: 1.7; }
.attachment { display: flex; gap: 10px; margin-top: 14px; align-items: flex-start; }
.attachment img, .attachment video { width: 104px; max-height: 104px; object-fit: contain; background: #f5f7fa; border-radius: 4px; }
.attachment-info { flex: 1; min-width: 0; overflow-wrap: anywhere; font-size: 12px; }
.attachment-info small { display: block; color: #8791a3; margin-top: 5px; }
.attachment-info .error { color: #d94848; }
</style>
